import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConnectedEmail } from 'app/models/connected-email';
import { EmailTrigger } from 'app/models/email-trigger';
import { ConnectedEmailService } from 'app/services/connected-email.service';
import { EmailTriggerService } from 'app/services/email-trigger.service';
import { ModalDirective } from 'ng-uikit-pro-standard';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-email-triggers',
  templateUrl: './email-triggers.component.html',
  styleUrls: ['./email-triggers.component.scss']
})
export class EmailTriggersComponent implements OnInit {

  tableLimitSelect = [5, 10, 20, 30, 50, 100];
  tableLoadingIndicator: boolean = true;
  selectedTableLimit = 5;

  connectedEmails: ConnectedEmail[] = [];
  emailFolders: string[] = [];

  emailTriggers: EmailTrigger[] = [];
  emailTriggerForm: FormGroup;

  emailTriggerToDelete: EmailTrigger = null;
  isDeleting: boolean = false;

  isAddingEmailTrigger: boolean = false;
  getFoldersSubscription: Subscription = null;

  @ViewChild('addEditEmailTriggerModal') addEditEmailTriggerModal: ModalDirective;
  @ViewChild('deleteConfirmationModal') deleteConfirmationModal: ModalDirective;

  isLoadingFolders: boolean = false;
  constructor(private emailTriggerService: EmailTriggerService,
              private connectedEmailService: ConnectedEmailService,
              private fb: FormBuilder)
            {
            }

  ngOnInit(): void {
    this.loadEmailTriggers();
    this.loadConnectedEmails();
  }

  loadConnectedEmails(): void {
    this.connectedEmailService.getAll().subscribe(data => {
      this.connectedEmails = data;
    });
  }

  onConnectedEmailsChange($event): void {
    const ids = $event.map(e => e.id);
    this.isLoadingFolders = true;
    this.emailFolders = [];
    if (this.getFoldersSubscription) {
      // cancel the request
      this.getFoldersSubscription.unsubscribe();
    }

    this.getFoldersSubscription = this.connectedEmailService.getFolders(ids).pipe(finalize(() => {
      this.isLoadingFolders = false;
      this.getFoldersSubscription = null;
    })).subscribe(data => {
      this.emailFolders = data;
    });
  }

  loadEmailTriggers(): void {
    this.tableLoadingIndicator = true;
    this.emailTriggerService.getAll().subscribe(data => {
      this.emailTriggers = data;
      this.tableLoadingIndicator = false;
    });
  }

  initForm(emailTrigger: EmailTrigger = null): void {
    this.emailTriggerForm = this.fb.group({
      id: [emailTrigger?.id || null],
      trigger_name: [emailTrigger?.trigger_name || '', [Validators.required]],
      folders: [emailTrigger?.folders || [], [Validators.required]],
      script: [emailTrigger?.script || '', [Validators.required]],
      last_processed: [this.formatDate(emailTrigger?.last_processed) || null, []],
      process_only_unread: [emailTrigger?.process_only_unread || false, []],
      connected_emails: [emailTrigger?.connected_emails || [], [Validators.required]]
    });
  }

  // Format date to local datetime-local input format (YYYY-MM-DDTHH:MM)
  // You need to pad the month, day, hour, and minute if they are less than 10.
  padTo2Digits(num: number) {
    return num.toString().padStart(2, '0');
  }

  formatDate(dateISOStr: string) {
    if (!dateISOStr) {
      return null;
    }

    let date = new Date(dateISOStr);

    return [
      date.getFullYear(),
      this.padTo2Digits(date.getMonth() + 1), // Note: Months are 0-based in JavaScript Date objects
      this.padTo2Digits(date.getDate()),
    ].join('-') +
    'T' +
    [
      this.padTo2Digits(date.getHours()),
      this.padTo2Digits(date.getMinutes()),
    ].join(':');
  }

  innerWidth(): number {
    return window.innerWidth;
  }

  openModal(emailTrigger: EmailTrigger = null): void {
    this.initForm(emailTrigger);
    this.addEditEmailTriggerModal.show();
  }

  deleteEmailTrigger(): void {
    this.isDeleting = true;
    this.emailTriggerService.delete(this.emailTriggerToDelete.id).pipe(finalize(() => {
      this.isDeleting = false;
    })).subscribe(() => {
      this.emailTriggers = this.emailTriggers.filter(s => s.id !== this.emailTriggerToDelete.id);
      this.emailTriggers = [...this.emailTriggers];
      this.deleteConfirmationModal.hide();
    });
  }

  onFinishedAddEditEmailTrigger() {
    if (this.emailTriggerForm.valid) {
      const data: EmailTrigger = this.emailTriggerForm.value;

      if (data.last_processed) {
        // Assume data.last_processed is in 'YYYY-MM-DDTHH:MM' format as from datetime-local input
        var localDateTimeString = data.last_processed;
      
        // Create a new Date object using the local date-time string
        var date = new Date(localDateTimeString);
      
        // Convert to ISO string (UTC)
        var isoDateTimeString = date.toISOString();
      
        // Now isoDateTimeString can be sent to the server
        data.last_processed = isoDateTimeString;
      }

      if (data.id) {
        this.isAddingEmailTrigger = true;
        this.emailTriggerService.update(data.id, data).pipe(finalize(() => {
          this.isAddingEmailTrigger = false;
        })).subscribe(updatedEmailTrigger => {
          const index = this.emailTriggers.findIndex(s => s.id === updatedEmailTrigger.id);
          this.emailTriggers[index] = updatedEmailTrigger;
          this.emailTriggers = [...this.emailTriggers];
          this.addEditEmailTriggerModal.hide();
        });
      } else {
        this.isAddingEmailTrigger = true;
        this.emailTriggerService.create(data).pipe(finalize(() => {
          this.isAddingEmailTrigger = false;
        })).subscribe(newEmailTrigger => {
          this.emailTriggers.push(newEmailTrigger);
          this.emailTriggers = [...this.emailTriggers];
          this.addEditEmailTriggerModal.hide();
        });
      }
    }
  }
}
  
